import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";
import { FaChevronDown } from "react-icons/fa";
import supabase from "../../supabase";

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('*')
          .eq('id', orderId)
          .maybeSingle();
        if (error) throw error;

        // If you add order_items, you can fetch them here and join product names
        let items = [];
        if (data?.order_id) {
          const { data: orderItems } = await supabase
            .from('order_items')
            .select('id, product_id, quantity, unit_price')
            .eq('order_id', data.order_id);
          if (orderItems) {
            const productIds = orderItems.map(i => i.product_id).filter(Boolean);
            let names = new Map();
            if (productIds.length) {
              const { data: prods } = await supabase.from('products').select('id, product_name, name').in('id', productIds);
              names = new Map((prods || []).map(p => [p.id, p.product_name || p.name]));
            }
            items = orderItems.map(i => ({
              id: i.id,
              product_name: names.get(i.product_id) || `#${i.product_id}`,
              quantity: i.quantity,
              price: Number(i.unit_price) || 0,
            }));
          }
        }

        setOrder({
          id: data?.id,
          customer_name: data?.customer_name || 'Customer',
          customer_email: data?.customer_email || '-',
          customer_phone: data?.customer_phone || '-',
          shipping_address: data?.shipping_address || '-',
          order_status: (data?.status || 'processing').toString().replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()),
          payment_status: data?.status || '-',
          total_amount: Number(data?.amount) || 0,
          items,
          created_at: data?.created_at,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        toast.error('Failed to load order');
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      setLoading(true);
      await supabase.from('payments').update({ status: newStatus.toLowerCase() }).eq('id', order.id);
      setOrder({ ...order, order_status: newStatus, payment_status: newStatus });
      toast.success("Order status updated successfully");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <TopBar />
        <div className="flex justify-center items-center h-full">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page">
        <TopBar />
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-2xl font-bold mb-4">Order not found</h1>
            <button
              onClick={() => navigate("/orders")}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <TopBar />
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">Order #{order.id}</h1>
              <div className="relative">
                <select
                  value={order.order_status}
                  onChange={(e) => handleStatusUpdate(e.target.value)}
                  className="appearance-none bg-gray-100 border rounded-lg pl-4 pr-10 py-2 cursor-pointer hover:bg-gray-200 transition-colors"
                >
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Customer Details</h2>
              <p><strong>Name:</strong> {order.customer_name}</p>
              <p><strong>Email:</strong> {order.customer_email}</p>
              <p><strong>Phone:</strong> {order.customer_phone}</p>
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2">Shipping Details</h2>
              <p><strong>Address:</strong> {order.shipping_address}</p>
              <p><strong>Status:</strong> <span className="text-blue-600">{order.order_status}</span></p>
              <p><strong>Payment:</strong> {order.payment_status}</p>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Order Items</h2>
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2">Quantity</th>
                  <th className="text-right py-2">Price</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="py-2">{item.product_name}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">${item.price}</td>
                    <td className="text-right">${item.quantity * item.price}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td colSpan="3" className="text-right py-2">Total Amount:</td>
                  <td className="text-right">${order.total_amount}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <button
            onClick={() => navigate("/orders")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Back to Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
